/* eslint-disable @typescript-eslint/consistent-type-definitions */
import {
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  NgZone,
  Input,
  Signal,
  input,
  computed,
} from '@angular/core';
import { map, Observable, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { StartingPriceModel } from '@app/shared/models/api/starting-price.model';
import { ApiError } from '@app/shared/models/api/api-error.model';
import { AkitaStartingPricesQuery } from '@app/akita/api/starting-prices/state/starting-prices.query';
import { AkitaStartingPricesService } from '@app/akita/api/starting-prices/state/starting-prices.service';
import {
  AllowedDeliveryCountries,
  LIST_OF_ALLOWED_DELIVERY_COUNTRY_CODES,
} from '@app/shared/data/delivery-countries.data';
import { InstallmentsDarkIconComponent } from '@app/shared/components/icons/generated/installments-dark/installments-dark.component';
import { WarrantyDarkIconComponent } from '@app/shared/components/icons/generated/warranty-dark/warranty-dark.component';
import { ReturnDarkIconComponent } from '@app/shared/components/icons/generated/return-dark/return-dark.component';

import { TranslocoModule } from '@ngneat/transloco';
import { CommonModule } from '@angular/common';
import { WARRANTY_MONTHS } from '@app/home/components/home-highlights/home-highlights.component';
import { ProductModelItemComponent } from './components/subcategory-list/product-model-item.component';
import { BrandIconPictureComponent } from '@app/shared/components/brand-icon-picture/brand-icon-picture.component';
import { toSignal } from '@angular/core/rxjs-interop';

interface StartingPriceModelByBrand {
  [brand: string]: Array<StartingPriceModel>;
}

@Component({
  selector: 'app-phone-brands',
  templateUrl: './phone-brands.template.html',
  styleUrls: ['./phone-brands.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoModule,
    ProductModelItemComponent,
    ReturnDarkIconComponent,
    WarrantyDarkIconComponent,
    InstallmentsDarkIconComponent,
    BrandIconPictureComponent,
  ],
})
export class PhoneBrandsComponent implements OnDestroy {
  public category = input<Array<string> | null>([]);
  public brand = input<Array<string> | null>([]);
  @Input() public collectionPageMode?: boolean | null = true;

  public Object = Object;

  public lessThan$: Observable<ScreenSizeInfoState>;
  public biggerThan$: Observable<ScreenSizeInfoState>;
  public inFullScreenMode$: Observable<boolean>;
  private readonly destroySubject: Subject<void>;

  public startingPrices: Signal<Array<StartingPriceModel>>;
  public sortedStartingPrices: Signal<StartingPriceModelByBrand>;
  public sortedBrandIndexes: Signal<Array<string>>;

  public fetchingStartingPrices$: Observable<boolean>;
  public startingPricesError$: Observable<ApiError | null>;

  public country: string;

  public months: string = WARRANTY_MONTHS;

  public readonly subscriptions: Subscription;

  constructor(
    private readonly zone: NgZone,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaStartingPricesQuery: AkitaStartingPricesQuery,
    private readonly akitaStartingPricesService: AkitaStartingPricesService
  ) {
    this.subscriptions = new Subscription();
    this.destroySubject = new Subject();
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();

    this.country = this.validateCountryCode(akitaRouterQuery.country);

    this.inFullScreenMode$ = this.akitaRouterQuery.selectInFullScreenMode();

    this.subscriptions.add(
      this.akitaRouterQuery
        .selectCountry()
        .pipe(
          map((value) => (value = this.validateCountryCode(value))),
          tap((value) => {
            if (this.country !== value) {
              this.country = value;
              this.subscriptions.add(
                this.akitaStartingPricesService.findStartingPricesAsync(value)
              );
            }
          }),
          takeUntil(this.destroySubject)
        )
        .subscribe({
          next: () => {},
          error: () => {},
        })
    );

    this.startingPrices = toSignal(this.akitaStartingPricesQuery.selectStartingPrices(), {
      initialValue: [],
    });

    this.sortedStartingPrices = computed(() => {
      const sorted: StartingPriceModelByBrand = {};
      let results = this.startingPrices().filter((x) =>
        this.category()?.length ? this.category()?.includes(x.category) : true
      );

      results = results.filter((x) =>
        this.brand()?.length ? this.brand()?.includes(x.brand) : true
      );

      results = results.filter((x) => x.inStock);

      results.forEach((x) => {
        if (!sorted[x.brand]) {
          sorted[x.brand] = [];
        }

        sorted[x.brand].push(x);
      });

      console.log('sorted', sorted);

      return sorted;
    });

    this.sortedBrandIndexes = computed(() =>
      this.getBrandsFromProducts(this.sortedStartingPrices())
    );

    this.fetchingStartingPrices$ =
      this.akitaStartingPricesQuery.selectFetchingStartingPrices();
    this.startingPricesError$ = this.akitaStartingPricesQuery.selectStartingPricesError();

    // if we do not have products on the state
    if (!this.akitaStartingPricesQuery.getStartingPrices().length) {
      if (this.akitaRouterQuery.isBrowser) {
        this.zone.runOutsideAngular(this.fetchStartingPricesInBG.bind(this));
      } else {
        this.fetchStartingPricesInBG();
      }
    }
  }

  private fetchStartingPricesInBG(): void {
    this.subscriptions.add(
      this.akitaStartingPricesService.findStartingPricesAsync(this.country)
    );
  }

  public getImageUrl(brand: string): string {
    // eslint-disable-next-line max-len
    return `https://firebasestorage.googleapis.com/v0/b/popsygae.appspot.com/o/Png-Brands-Images%2F${brand}.png?alt=media&token=14f6dec7-6448-4f64-8c1f-6919068d9007`;
  }

  public trackByModel(index: number, category?: any | null): string {
    return (category as any)?.id || category || `${index}`;
  }

  public validateCountryCode(country: string): string {
    if (country) {
      country = country.toUpperCase() || '';
      if (
        LIST_OF_ALLOWED_DELIVERY_COUNTRY_CODES.indexOf(
          `${country || ''}`.toUpperCase() as AllowedDeliveryCountries
        ) === -1
      ) {
        country = 'US'; // fallback
      }
    }

    return country;
  }

  private getBrandsFromProducts(products: StartingPriceModelByBrand): Array<string> {
    const brandIndexes: Array<string> = new Array(0);

    Object.keys(products).forEach((brand) => {
      if (!brandIndexes.includes(brand)) {
        brandIndexes.push(brand);
      }
    });

    if (brandIndexes.includes('Samsung')) {
      brandIndexes.splice(brandIndexes.indexOf('Samsung'), 1);
      brandIndexes.unshift('Samsung');
    }
    if (brandIndexes.includes('Apple')) {
      brandIndexes.splice(brandIndexes.indexOf('Apple'), 1);
      brandIndexes.unshift('Apple');
    }

    return brandIndexes;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
